// "Let the bot message you" — the flow behind NotifyPermissionPrompt.
//
// ask → Allow → Telegram's native dialog → the server sends a real thank-you
// message (POST /user/notifications/verify) → granted (reward credited once) or
// failed (try again / skip). "No" asks once more before letting go.
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { verifyNotificationsUserNotificationsVerifyPost } from '../../client';
import type { UserData } from '../../models/user';
import { requestWriteAccessAsync } from '../../utils/telegram/telegramWriteAccess';
import { impactOccurred, notificationOccurred } from '../../utils/telegram/hapticFeedback';

export type NotifySource = 'onboarding' | 'house_purchase';
export type NotifyPhase = 'ask' | 'verifying' | 'granted' | 'failed' | 'confirmDecline';

export interface NotifyReward {
  pearls: number;
  exp: number;
}

export interface NotifyPermission {
  phase: NotifyPhase;
  reward: NotifyReward;
  allow: () => void;
  decline: () => void;
  /** From failed: ask again. */
  retry: () => void;
  /** The player leaves without allowing ('declined' after "Are you sure?", or skipped after a failure). */
  giveUp: (how: 'declined' | 'skipped') => void;
}

const track = (event: string, props: Record<string, unknown>) => {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics is optional */
  }
};

export function useNotifyPermission(source: NotifySource, onGranted?: (reward: NotifyReward) => void): NotifyPermission {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<NotifyPhase>('ask');
  const [reward, setReward] = useState<NotifyReward>({ pearls: 0, exp: 0 });

  useEffect(() => {
    track('notify_prompt_shown', { source });
  }, [source]);

  const allow = useCallback(() => {
    impactOccurred('medium');
    track('notify_allow_tapped', { source });
    setPhase('verifying');
    void (async () => {
      const granted = await requestWriteAccessAsync();
      track('notify_native_result', { source, granted });
      if (!granted) {
        notificationOccurred('warning');
        setPhase('failed');
        return;
      }
      try {
        const { data } = await verifyNotificationsUserNotificationsVerifyPost({ body: { source }, throwOnError: true });
        track('notify_verified', { source, ok: data.ok, error: data.error ?? null, reward_pearls: data.reward_pearls ?? 0 });
        if (!data.ok) {
          notificationOccurred('warning');
          setPhase('failed');
          return;
        }
        const got = { pearls: data.reward_pearls ?? 0, exp: data.reward_exp ?? 0 };
        queryClient.setQueryData<UserData>(['userData'], (old) => (old
          ? {
            ...old,
            notifications_warmup_granted: true,
            currency_balance: data.new_balance ?? old.currency_balance,
          }
          : old));
        void queryClient.invalidateQueries({ queryKey: ['userData'] });
        setReward(got);
        notificationOccurred('success');
        setPhase('granted');
        onGranted?.(got);
      } catch (error) {
        track('notify_verified', { source, ok: false, error: error instanceof Error ? error.message : 'request failed' });
        notificationOccurred('warning');
        setPhase('failed');
      }
    })();
  }, [source, queryClient, onGranted]);

  const decline = useCallback(() => {
    impactOccurred('light');
    track('notify_declined', { source });
    setPhase('confirmDecline');
  }, [source]);

  const retry = useCallback(() => {
    impactOccurred('light');
    track('notify_retry', { source });
    setPhase('ask');
  }, [source]);

  const giveUp = useCallback((how: 'declined' | 'skipped') => {
    track(how === 'declined' ? 'notify_decline_confirmed' : 'notify_skipped', { source });
  }, [source]);

  return { phase, reward, allow, decline, retry, giveUp };
}

